/**
 * Playback: the recorded stick, run back through the real engine.
 *
 * There is no state streaming here and no interpolation of recorded positions.
 * The player builds the identical mission from the tape's own subject, feeds it
 * the identical stick tick for tick, and the aeroplane goes where it went
 * because the same physics put it there. That is what makes a tape a
 * *regression fixture* as well as a keepsake: if this build flies it somewhere
 * else, the checkpoint hashes say so.
 *
 * ## Seeking, for free
 *
 * The original could not seek backwards at all — "Rewind will only rewind the
 * tape to the beginning... It's all or nothing", and the Location Slider "cannot
 * be moved backward" (manual, Playback Controls). That was a consequence of
 * re-simulation on a 1990 machine, not a design choice, and this port does not
 * inherit it, for a reason that is specific to how the engine is built:
 * **`EncounterState` and `MissionRun` are immutable.** Every tick returns a
 * fresh state and never mutates the old one, so *keeping* the state from every
 * few seconds costs one reference each — no copying, no serialization, no
 * snapshot format. The seek index is therefore a plain array of states the
 * playback has already flown past.
 *
 * A scrub backwards restores the nearest kept state and re-simulates forward
 * from there, which is at most `SEEK_INDEX_TICKS` ticks of catch-up — a few
 * milliseconds — instead of the whole tape. A scrub forwards just keeps
 * running. Neither needs the tape to carry any state at all.
 */

import { createEncounter, stepEncounter, type EncounterState } from "../combat/encounter.ts";
import { encounterHash } from "../combat/encounter-hash.ts";
import { FLAT_GROUND, type GroundQuery } from "../flight/ground.ts";
import { NEUTRAL_INPUTS, type FlightInputs } from "../flight/inputs.ts";
import { FIXED_DT } from "../flight/units.ts";
import { createMissionRun, stepMissionRun, type MissionRun } from "../mission/mission-run.ts";
import { InputTrace } from "./input-trace.ts";
import type { ReplayTape } from "./replay-tape.ts";

/**
 * Ticks between kept states — ten seconds at 60 Hz.
 *
 * The trade is memory against scrub latency. Ten seconds means a backward scrub
 * re-simulates at most 600 ticks (a few milliseconds for a busy encounter) and
 * a fifteen-minute sortie keeps about ninety states. Halving it would double the
 * retained states to make an already-imperceptible scrub imperceptibly faster.
 */
export const SEEK_INDEX_TICKS = 600;

/** One tick of playback, kept so a scrub can rewind to it. */
interface KeptState {
  readonly tick: number;
  readonly encounter: EncounterState;
  readonly mission: MissionRun | null;
}

export interface ReplayVerification {
  /** Checkpoints reached and compared so far. */
  readonly checked: number;
  /** The first tick whose hash did not match, or `null` while the replay is faithful. */
  readonly divergedAt: number | null;
}

/**
 * Drives one tape. Owns the simulation, the seek index and the divergence
 * ledger; owns no clock, no renderer and no notion of "speed" — how fast to
 * call `advance` is the caller's business, which is what lets the same object
 * serve the VCR's playback bar and a headless regression test.
 */
export class ReplayPlayer {
  private readonly trace: InputTrace;
  private readonly expected: Map<number, string>;
  private readonly index: KeptState[] = [];
  private live: KeptState;
  private previous: EncounterState;
  private checked = 0;
  private diverged: number | null = null;

  constructor(
    readonly tape: ReplayTape,
    private readonly ground: GroundQuery = FLAT_GROUND,
  ) {
    this.trace = new InputTrace(tape.trace);
    this.expected = new Map(tape.checkpoints.map(([tick, hash]) => [tick, hash]));
    this.live = startOf(tape);
    this.previous = this.live.encounter;
    this.index.push(this.live);
  }

  /** Ticks in the whole tape. */
  get ticks(): number {
    return this.tape.ticks;
  }

  /** Where playback currently stands. */
  get tick(): number {
    return this.live.tick;
  }

  /** True once playback has reached the end of the tape. */
  get ended(): boolean {
    return this.live.tick >= this.tape.ticks;
  }

  encounter(): EncounterState {
    return this.live.encounter;
  }

  /**
   * The stick the tape actually flew to reach the current tick — neutral at
   * tick 0, before any tick has run. This is what a viewer-facing "what is the
   * flight doing right now" readout (the cockpit's painted control surfaces
   * and muzzle flash) must key off, never the viewer's own held keys: nothing
   * about the pilot's live keyboard flies the recording (see the module doc),
   * so presentation that watches it instead just goes blank.
   */
  currentInputs(): FlightInputs {
    return this.trace.at(this.live.tick) ?? NEUTRAL_INPUTS;
  }

  /** The state one tick back, for the renderer's interpolation. */
  priorEncounter(): EncounterState {
    return this.previous;
  }

  mission(): MissionRun | null {
    return this.live.mission;
  }

  verification(): ReplayVerification {
    return { checked: this.checked, divergedAt: this.diverged };
  }

  /**
   * Runs up to `steps` ticks, stopping at the end of the tape. Returns how many
   * actually ran, which is how a caller tells "playing" from "finished".
   */
  advance(steps: number): number {
    let ran = 0;
    while (ran < steps && !this.ended) {
      this.previous = this.live.encounter;
      this.live = this.stepOnce(this.live);
      this.verify(this.live.encounter);
      this.keep(this.live);
      ran += 1;
    }
    return ran;
  }

  /**
   * Moves playback to `tick`, re-simulating from the nearest kept state when
   * that means going backwards. Clamped to the tape.
   */
  seek(tick: number): void {
    const target = Math.max(0, Math.min(Math.round(tick), this.tape.ticks));
    if (target < this.live.tick) {
      this.live = this.nearestKept(target);
      this.previous = this.live.encounter;
    }
    // A scrub re-flies ticks that were already verified once. Re-checking them
    // would count the same checkpoint twice and, on a diverging tape, keep
    // re-proving the same divergence — so the ledger is left alone and only
    // `advance` feeds it.
    while (this.live.tick < target) {
      this.previous = this.live.encounter;
      this.live = this.stepOnce(this.live);
      this.keep(this.live);
    }
  }

  private stepOnce(from: KeptState): KeptState {
    const tick = from.tick + 1;
    const inputs = this.trace.at(tick) ?? NEUTRAL_INPUTS;
    const keyed = { [this.tape.playerId]: inputs };
    if (from.mission) {
      const mission = stepMissionRun(from.mission, keyed, FIXED_DT, this.ground);
      return { tick, encounter: mission.encounter, mission };
    }
    return {
      tick,
      encounter: stepEncounter(from.encounter, keyed, FIXED_DT, this.ground),
      mission: null,
    };
  }

  /**
   * Compares this tick against the tape when it carries a checkpoint for it.
   * The verdict is sticky, the way `net/hash-ledger.ts`'s is and for the same
   * reason: two runs that agree again later did not re-converge, they collided.
   */
  private verify(state: EncounterState): void {
    const want = this.expected.get(state.tick);
    if (want === undefined) return;
    this.checked += 1;
    if (want !== encounterHash(state) && this.diverged === null) this.diverged = state.tick;
  }

  private keep(state: KeptState): void {
    const last = this.index[this.index.length - 1];
    if (last && state.tick - last.tick < SEEK_INDEX_TICKS) return;
    this.index.push(state);
  }

  /** The newest kept state at or before `tick`. The index always holds tick 0, so this always finds one. */
  private nearestKept(tick: number): KeptState {
    let found = this.index[0];
    if (!found) throw new Error("replay seek index is empty");
    for (const kept of this.index) {
      if (kept.tick > tick) break;
      found = kept;
    }
    return found;
  }
}

/**
 * Tick zero. Neither constructor takes a `GroundQuery` — an aeroplane's spawn
 * is placed by the setup, and the terrain first matters on the tick after.
 */
function startOf(tape: ReplayTape): KeptState {
  if (tape.subject.kind === "mission") {
    const mission = createMissionRun(tape.subject.mission);
    return { tick: 0, encounter: mission.encounter, mission };
  }
  return { tick: 0, encounter: createEncounter(tape.subject.encounter), mission: null };
}

/**
 * Replays a whole tape as fast as the machine will run it and reports whether
 * every checkpoint still holds. This is the regression harness's one call.
 */
export function verifyTape(tape: ReplayTape, ground: GroundQuery = FLAT_GROUND): ReplayVerification {
  const player = new ReplayPlayer(tape, ground);
  player.advance(tape.ticks);
  return player.verification();
}
